import type { SiteLang } from "./content/index.js";
import { SELLER } from "./seller.js";

/**
 * Черновики Terms, Refund и Privacy для проверки Paddle и App Review у Meta. ТРЕБУЮТ ПРОВЕРКИ ЮРИСТОМ.
 * Английский текст Privacy продублирован в `docs/meta-app-review/privacy-policy.md`, правьте оба места.
 * Три языка: сербский главный, английский и русский с той же структурой (русский не юридический перевод,
 * а понятный текст для русскоязычного владельца; пометка об этом под заголовком каждого документа).
 * Платная позиция одна, подписка: пакет «Подключим за вас» с сайта и из документов убран (решение 05.11.2026, Paddle
 * принимает оплату только за цифровые продукты). Обязательная фраза Paddle в Terms (§3) не меняется.
 * Реквизиты продавца (имя ИП, регистрационный номер, адрес, применимое право) пока не публикуются: тексты от «мы»,
 * для связи только почта поддержки `SELLER.email`; поля в `seller.ts` оставлены, чтобы вернуть их одной правкой.
 */
export interface LegalSection {
  id?: string;
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface LegalDoc {
  title: string;
  intro: string;
  /** Пометка под заголовком: текст есть на трёх языках, при расхождениях главная сербская версия. */
  note: string;
  sections: LegalSection[];
}

export type LegalKind = "terms" | "refund" | "privacy";

const S = SELLER;

/** Пометка о языках под заголовком каждого документа. */
const EN_NOTE = "This text is available in Serbian, English and Russian. In case of any discrepancy, the Serbian version prevails.";
const SR_NOTE = "Tekst postoji na srpskom, engleskom i ruskom jeziku. U slučaju neslaganja merodavna je verzija na srpskom.";
const RU_NOTE = "Этот текст есть на сербском, английском и русском. Если версии расходятся, действует сербская.";

const en: Record<LegalKind, LegalDoc> = {
  terms: {
    title: "Terms of Service",
    intro: `These Terms govern the use of ${S.productName} (the “Service”) and apply between you and us, the provider of the Service (“we”, “us”). By creating an account or paying for a subscription, you agree to these Terms on behalf of your business.`,
    note: EN_NOTE,
    sections: [
      {
        heading: "1. The Service",
        paragraphs: [
          "The Service is booking software with AI replies for small businesses that work by appointment (salons, barbershops and similar). It includes a calendar, a staff app, a public booking page and an AI assistant that replies to clients' messages sent to the business's Instagram account and books appointments into the calendar using only information the business has entered.",
          "The Service is intended for businesses, not for consumers. It does not send bulk or unsolicited messages and does not collect prepayments from the business's clients.",
        ],
      },
      {
        heading: "2. Accounts",
        paragraphs: [
          "The business owner creates the account and may invite staff members. You are responsible for the people you invite and for keeping access to your email secure. You sign in with a one-time code sent to your email.",
        ],
      },
      {
        heading: "3. Subscription, trial and payment",
        list: [
          "The subscription costs €25 per month; any applicable tax is calculated by Paddle at checkout.",
          "New subscriptions may start with a 30-day free trial. A payment method is required to start the trial; you are first charged when the trial ends, unless you cancel before then.",
          "The subscription renews automatically every month until cancelled. You can cancel at any time from the billing page of the app or via the link in Paddle's emails; access continues until the end of the paid period.",
          "Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record (the official seller) for all our orders. Paddle provides all customer service inquiries and handles returns.",
        ],
      },
      {
        heading: "4. Acceptable use",
        list: [
          "Use the Service only for your own business and in line with applicable law and Instagram/Meta platform rules.",
          "Do not use the Service for marketing campaigns, bulk messaging, messaging people who did not contact you first, or collecting prepayments from clients.",
          "Inform your clients that their messages and appointments are processed by software providers on your behalf (for example via a link to a privacy notice in your profile).",
          "Keep the information in the Service (services, prices, rules) accurate: the AI assistant answers only from it.",
        ],
      },
      {
        heading: "5. AI replies",
        paragraphs: [
          "The AI assistant can make mistakes. It answers only from the business's data and hands the conversation to staff when it does not know the answer, when the client asks to speak to a person, or when a message is a complaint or about health. The assistant does not introduce itself as AI, but answers honestly when asked directly; you can turn on AI disclosure in settings. The business remains responsible for its communication with clients.",
        ],
      },
      {
        heading: "6. Data",
        paragraphs: [
          "For your clients' personal data (messages, names, phone numbers, appointments), the business is the controller and we act as a processor under a data processing agreement. How we handle data is described in our Privacy Policy.",
        ],
      },
      {
        heading: "7. Availability and changes",
        paragraphs: [
          "We aim to keep the Service available but do not guarantee uninterrupted operation. Instagram messaging depends on Meta, which may change or limit its platform. We may change the Service and these Terms; material changes are announced by email at least 14 days in advance.",
        ],
      },
      {
        heading: "8. Liability",
        paragraphs: [
          "To the extent permitted by law, our total liability for any claim is limited to the amounts you paid for the Service in the 3 months before the claim. We are not liable for lost profits, lost appointments or indirect damages.",
        ],
      },
      {
        heading: "9. Termination and your data",
        paragraphs: [
          "You can stop using the Service at any time. On request, we will send you your client list and appointments as a CSV file, and delete your data. After a deletion request the data is kept for 30 days in case you change your mind and is then erased permanently. A deletion request cancels the subscription immediately; if you withdraw the request within 30 days, your data is restored, but the Service stays inactive until you start a new subscription (without a free trial).",
        ],
      },
      { heading: "10. Contact", paragraphs: [`Support: ${S.email}.`] },
    ],
  },
  refund: {
    title: "Refund Policy",
    intro: `Payments for ${S.productName} are processed by Paddle.com, our Merchant of Record (the official seller). Refunds are issued by Paddle to the original payment method.`,
    note: EN_NOTE,
    sections: [
      { heading: "Trial", paragraphs: ["If you cancel during the free trial, you are not charged for the subscription."] },
      {
        heading: "Subscription",
        list: [
          "You can cancel at any time; the subscription stays active until the end of the paid month and is not renewed.",
          "If you are not satisfied, you can request a full refund of your first monthly payment within 14 days of that payment.",
          "Later monthly payments are not refunded for partially used periods, except where required by law.",
        ],
      },
      {
        heading: "How to request a refund",
        paragraphs: [
          `Email ${S.email} or reply to your Paddle receipt email, and include your order number. Paddle may also review refund requests under its own buyer terms.`,
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: `This policy explains how we, the provider of ${S.productName} (the “Service”), process personal data in the Service, including data received through the Instagram Platform.`,
    note: EN_NOTE,
    sections: [
      {
        heading: "1. Roles",
        paragraphs: [
          "For the accounts of salon owners and staff, we are the controller. For the data of the salon's clients (messages, names, phone numbers, appointments), the salon is the controller and we process the data on its behalf under a data processing agreement.",
        ],
      },
      {
        heading: "2. Data we process",
        list: [
          "Account data: email address, name, language, sign-in sessions.",
          "Salon data: services, prices, staff, schedules, rules and answers to frequently asked questions.",
          "Instagram data (via the Instagram API with Unified Login, also called Facebook Login for Business, after the salon connects its professional account): the account ID and username, an access token (stored encrypted), messages sent to the salon by its clients and replies sent from the Service, the clients' Instagram-scoped IDs and usernames.",
          "Appointments and client records: name, phone number (if provided), visit history, notes, no-shows.",
          "Voice messages are transcribed to text and the audio is not stored. Photos and other files are not stored.",
          "Payment data is collected by Paddle; we receive only the subscription status, not card details.",
          "Technical logs and usage records (for example, the number of AI tokens used per salon).",
        ],
      },
      {
        heading: "3. How we use the data",
        list: [
          "To answer the salon's clients and book appointments on the salon's instructions.",
          "To show conversations, appointments and notifications to the salon's staff.",
          "To charge for the subscription, keep the Service secure, monitor errors and support the salon.",
          "Messages about health (for example, allergies) are passed to staff and are not sent to the AI model.",
          "We do not sell data, do not use it for advertising and do not use Instagram data for any purpose other than providing the Service to the salon that connected the account.",
        ],
      },
      {
        heading: "4. Service providers",
        paragraphs: [
          "We use: Anthropic (AI model, USA), OpenAI (voice transcription, USA), Railway (hosting in the EU West region, with processing in the USA under Railway's terms), Resend (email), Paddle (payments, Merchant of Record), Meta (Instagram messaging). Some of them process data outside Serbia and the EU, including in the USA.",
        ],
      },
      {
        heading: "5. Retention",
        list: [
          "Salon and client data is kept while the salon uses the Service.",
          "The salon's data is permanently erased 30 days after a deletion request.",
          "Technical webhook records are kept for 90 days, AI usage records for 180 days.",
        ],
      },
      {
        id: "data-deletion",
        heading: "6. How to delete your data",
        list: [
          `Salons: write to ${S.email} from the owner's email address and ask to delete the account. We will confirm the request within 7 days; the data is erased permanently 30 days after the request. You can also remove our access yourself at any time: in Instagram under Settings and activity → Website permissions → Apps and websites, or in the settings of your Meta business portfolio (connected apps).`,
          `Clients of a salon: contact the salon, or write to ${S.email} with your Instagram username and the salon's name; we will forward the request to the salon and delete the data on its instruction.`,
        ],
      },
      {
        heading: "7. Your rights",
        paragraphs: [
          "You have the right to access, correct or delete your data, to restrict its processing, to data portability and to object to processing. You can also lodge a complaint with the Commissioner for Information of Public Importance and Personal Data Protection of Serbia (poverenik.rs).",
        ],
      },
      { heading: "8. Contact", paragraphs: [`Support: ${S.email}.`] },
    ],
  },
};

const sr: Record<LegalKind, LegalDoc> = {
  terms: {
    title: "Uslovi korišćenja",
    intro: `Ovi Uslovi uređuju korišćenje usluge ${S.productName} („Usluga“) i važe između vas i nas, pružaoca Usluge („mi“). Otvaranjem naloga ili plaćanjem pretplate prihvatate ove Uslove u ime svog salona (u daljem tekstu „salon“: salon, berbernica ili slična radnja).`,
    note: SR_NOTE,
    sections: [
      {
        heading: "1. Usluga",
        paragraphs: [
          "Usluga je softver za zakazivanje termina sa AI odgovorima za salone, berbernice i slične radnje koje rade po zakazivanju. Obuhvata kalendar, aplikaciju za majstore, javnu stranicu za zakazivanje i AI asistenta koji odgovara na poruke koje klijenti šalju na Instagram nalog salona i upisuje termine u kalendar, isključivo na osnovu podataka koje je salon uneo.",
          "Usluga je namenjena preduzetnicima i pravnim licima, a ne potrošačima. Ne šalje masovne ni neželjene poruke i ne naplaćuje avanse klijentima salona.",
        ],
      },
      {
        heading: "2. Nalozi",
        paragraphs: [
          "Vlasnik otvara nalog i može da pozove majstore. Odgovorni ste za osobe koje pozovete i za bezbednost svog imejl naloga. Prijavljujete se jednokratnim kodom koji stiže na imejl.",
        ],
      },
      {
        heading: "3. Pretplata, probni period i plaćanje",
        list: [
          "Pretplata košta 25 € mesečno; porez, ako se obračunava, Paddle računa pri plaćanju.",
          "Nova pretplata može početi besplatnim probnim periodom od 30 dana. Za početak probnog perioda potrebno je uneti način plaćanja; prva mesečna naplata vrši se po isteku probnog perioda, osim ako pretplatu ranije otkažete.",
          "Pretplata se obnavlja automatski svakog meseca dok je ne otkažete. Otkazati možete u svakom trenutku na stranici pretplate u aplikaciji ili preko linka iz imejlova koje šalje Paddle; pristup traje do kraja plaćenog perioda.",
          "Porudžbine obrađuje naš onlajn preprodavac Paddle.com. Paddle.com je Merchant of Record (zvanični prodavac) za sve naše porudžbine i obrađuje upite kupaca i zahteve za povrat novca.",
        ],
      },
      {
        heading: "4. Dozvoljeno korišćenje",
        list: [
          "Koristite Uslugu samo za svoj salon, u skladu sa zakonom i pravilima Instagrama i kompanije Meta.",
          "Ne koristite Uslugu za marketinške kampanje, masovne poruke, pisanje osobama koje vam se nisu prve javile ni za naplatu avansa od klijenata.",
          "Obavestite klijente da njihove poruke i termine u vaše ime obrađuju pružaoci softvera (na primer linkom ka obaveštenju o privatnosti u profilu).",
          "Vodite računa da podaci (usluge, cene, pravila) budu tačni: AI asistent odgovara samo na osnovu njih.",
        ],
      },
      {
        heading: "5. AI odgovori",
        paragraphs: [
          "AI asistent može da pogreši. Odgovara samo na osnovu podataka salona i predaje razgovor majstoru kada ne zna odgovor, kada klijent traži da razgovara sa čovekom (nekim iz salona) ili kada se klijent žali ili piše o zdravlju. Asistent sam ne kaže da je AI, ali na direktno pitanje odgovara iskreno; u podešavanjima možete uključiti opciju da se asistent odmah predstavi kao AI. Salon ostaje odgovoran za komunikaciju sa klijentima.",
        ],
      },
      {
        heading: "6. Podaci",
        paragraphs: [
          "Za podatke o ličnosti vaših klijenata (poruke, imena, brojevi telefona, termini) salon je rukovalac, a mi obrađivač na osnovu ugovora o obradi podataka o ličnosti. Način obrade opisan je u Politici privatnosti.",
        ],
      },
      {
        heading: "7. Dostupnost i izmene",
        paragraphs: [
          "Trudimo se da Usluga bude dostupna, ali ne garantujemo neprekidan rad. Poruke na Instagramu zavise od kompanije Meta, koja može da menja ili ograničava svoju platformu. Uslugu i ove Uslove možemo menjati; o bitnim izmenama obaveštavamo imejlom najmanje 14 dana unapred.",
        ],
      },
      {
        heading: "8. Odgovornost",
        paragraphs: [
          "U meri u kojoj zakon dozvoljava, naša ukupna odgovornost ograničena je na iznose koje ste platili za Uslugu u poslednja 3 meseca pre podnošenja odštetnog zahteva. Ne odgovaramo za izmaklu korist, izgubljene termine ni posrednu štetu.",
        ],
      },
      {
        heading: "9. Prestanak i vaši podaci",
        paragraphs: [
          "Možete prestati da koristite Uslugu u svakom trenutku. Na zahtev vam šaljemo spisak klijenata i termina u CSV formatu i brišemo vaše podatke. Posle zahteva za brisanje podaci se čuvaju 30 dana u slučaju da se predomislite, a zatim se trajno brišu. Podnošenjem zahteva za brisanje pretplata se odmah otkazuje; ako zahtev povučete u roku od 30 dana, podaci se vraćaju, ali Usluga ostaje neaktivna dok ne pokrenete novu pretplatu (bez probnog perioda).",
        ],
      },
      { heading: "10. Kontakt", paragraphs: [`Podrška: ${S.email}.`] },
    ],
  },
  refund: {
    title: "Politika povrata novca",
    intro: `Plaćanja za ${S.productName} obrađuje Paddle.com, naš Merchant of Record (zvanični prodavac). Novac vraća Paddle, na isti način na koji ste platili.`,
    note: SR_NOTE,
    sections: [
      { heading: "Probni period", paragraphs: ["Ako otkažete tokom besplatnog probnog perioda, pretplata se ne naplaćuje."] },
      {
        heading: "Pretplata",
        list: [
          "Otkazati možete u svakom trenutku; pretplata važi do kraja plaćenog meseca i ne obnavlja se.",
          "Ako niste zadovoljni, možete tražiti povraćaj celog iznosa prve mesečne uplate u roku od 14 dana od te uplate.",
          "Za kasnije mesečne uplate ne vraćamo novac za delimično iskorišćen period, osim kada to zakon zahteva.",
        ],
      },
      {
        heading: "Kako zatražiti povrat novca",
        paragraphs: [
          `Pišite na ${S.email} i navedite broj porudžbine ili odgovorite na imejl sa računom koji vam je poslao Paddle. Paddle može razmotriti zahtev i prema svojim uslovima za kupce.`,
        ],
      },
    ],
  },
  privacy: {
    title: "Politika privatnosti",
    intro: `Ova politika objašnjava kako mi, pružalac usluge ${S.productName} („Usluga“), obrađujemo podatke o ličnosti u Usluzi, uključujući podatke dobijene preko Instagram platforme.`,
    note: SR_NOTE,
    sections: [
      {
        heading: "1. Uloge",
        paragraphs: [
          "Za naloge vlasnika i majstora salona mi smo rukovalac. Za podatke klijenata salona (poruke, imena, brojevi telefona, termini) rukovalac je salon, a mi ih obrađujemo u njegovo ime na osnovu ugovora o obradi podataka o ličnosti.",
        ],
      },
      {
        heading: "2. Koje podatke obrađujemo",
        list: [
          "Podaci naloga: imejl adresa, ime, jezik, sesije prijave.",
          "Podaci salona: usluge, cene, majstori, rasporedi, pravila i odgovori na česta pitanja.",
          "Podaci sa Instagrama (preko Instagram API-ja i prijave Unified Login, poznate i kao Facebook Login for Business, kada salon poveže profesionalni nalog): ID i korisničko ime naloga, pristupni token (čuva se u šifrovanom obliku), poruke koje klijenti šalju salonu i odgovori poslati iz Usluge, Instagram ID-jevi i korisnička imena klijenata.",
          "Termini i kartoni klijenata: ime, broj telefona (ako ga je klijent ostavio), istorija poseta, beleške, nedolasci.",
          "Glasovne poruke se pretvaraju u tekst, a zvuk se ne čuva. Fotografije i drugi fajlovi se ne čuvaju.",
          "Podatke o plaćanju prikuplja Paddle; mi dobijamo samo status pretplate, ne i podatke o kartici.",
          "Tehnički zapisi i evidencija korišćenja (na primer, broj AI tokena po salonu).",
        ],
      },
      {
        heading: "3. Kako koristimo podatke",
        list: [
          "Da odgovorimo klijentima salona i zakažemo termine po uputstvima salona.",
          "Da prikažemo razgovore, termine i obaveštenja majstorima salona.",
          "Da naplatimo pretplatu, zaštitimo Uslugu, pratimo greške i pružimo podršku salonu.",
          "Poruke o zdravlju (na primer, alergije) prosleđuju se majstoru i ne šalju se AI modelu.",
          "Podatke ne prodajemo i ne koristimo za reklame. Podatke sa Instagrama koristimo samo za pružanje Usluge salonu koji je povezao nalog.",
        ],
      },
      {
        heading: "4. Pružaoci usluga",
        paragraphs: [
          "Koristimo: Anthropic (AI model, SAD), OpenAI (prepoznavanje govora, SAD), Railway (hosting, region EU West uz obradu u SAD prema uslovima Railway-a), Resend (imejl), Paddle (plaćanja, Merchant of Record), Meta (Instagram poruke). Neki od njih obrađuju podatke van Srbije i EU, uključujući i SAD.",
        ],
      },
      {
        heading: "5. Čuvanje",
        list: [
          "Podaci salona i klijenata čuvaju se dok salon koristi Uslugu.",
          "Podaci salona se trajno brišu 30 dana posle zahteva za brisanje.",
          "Tehnički zapisi webhook-ova čuvaju se 90 dana, podaci o potrošnji AI tokena 180 dana.",
        ],
      },
      {
        id: "data-deletion",
        heading: "6. Kako obrisati podatke",
        list: [
          `Saloni: pišite na ${S.email} sa imejl adrese vlasnika i zatražite brisanje naloga. Zahtev potvrđujemo u roku od 7 dana; podaci se trajno brišu 30 dana posle zahteva. Pristup možete ukinuti i sami u svakom trenutku: na Instagramu u Podešavanja i aktivnost → Dozvole za veb-sajtove → Aplikacije i veb-sajtovi, ili u podešavanjima svog Meta poslovnog portfolija (povezane aplikacije).`,
          `Klijenti salona: obratite se salonu ili pišite na ${S.email} i navedite svoje korisničko ime na Instagramu i naziv salona; zahtev prosleđujemo salonu i brišemo podatke po njegovom uputstvu.`,
        ],
      },
      {
        heading: "7. Vaša prava",
        paragraphs: [
          "Imate pravo na pristup, ispravku i brisanje podataka, ograničenje obrade i prenosivost podataka, kao i pravo na prigovor. Pritužbu možete podneti Povereniku za informacije od javnog značaja i zaštitu podataka o ličnosti (poverenik.rs).",
        ],
      },
      { heading: "8. Kontakt", paragraphs: [`Podrška: ${S.email}.`] },
    ],
  },
};

const ru: Record<LegalKind, LegalDoc> = {
  terms: {
    title: "Условия использования",
    intro: `Эти условия определяют, как пользоваться сервисом ${S.productName} («Сервис»), и действуют между вами и нами, поставщиком Сервиса («мы»). Создавая аккаунт или оплачивая подписку, вы принимаете эти условия от имени своего бизнеса (далее «салон»).`,
    note: RU_NOTE,
    sections: [
      {
        heading: "1. Сервис",
        paragraphs: [
          "Сервис это программа онлайн-записи с ИИ-ассистентом для салонов, барбершопов и другого малого бизнеса, где клиенты приходят по записи. В него входят календарь, приложение для мастеров, публичная страница записи и ИИ-ассистент, который отвечает на сообщения, которые клиенты пишут салону в Instagram, и вносит записи в календарь, опираясь только на данные, которые салон указал сам.",
          "Сервис предназначен для бизнеса, а не для частных лиц. Он не делает массовых рассылок и спама и не берёт предоплату с клиентов салона.",
        ],
      },
      {
        heading: "2. Аккаунты",
        paragraphs: [
          "Аккаунт создаёт владелец салона; он может пригласить мастеров. Вы отвечаете за людей, которых пригласили, и за сохранность доступа к своей почте. Вход выполняется по одноразовому коду, который приходит на почту.",
        ],
      },
      {
        heading: "3. Подписка, пробный период и оплата",
        list: [
          "Подписка стоит €25 в месяц; если применяется налог, Paddle рассчитывает его при оплате.",
          "Новая подписка может начинаться с бесплатного пробного периода в 30 дней. Чтобы начать пробный период, нужно привязать карту; первое списание будет после его окончания, если вы не отмените подписку раньше.",
          "Подписка продлевается автоматически каждый месяц, пока вы её не отмените. Отменить можно в любой момент на странице подписки в приложении или по ссылке из писем Paddle; доступ сохраняется до конца оплаченного периода.",
          "Заказы обрабатывает Paddle.com, наш онлайн-реселлер и официальный продавец (Merchant of Record) по всем нашим заказам. Paddle отвечает на вопросы покупателей и оформляет возвраты.",
        ],
      },
      {
        heading: "4. Допустимое использование",
        list: [
          "Используйте Сервис только для своего салона, в соответствии с законом и правилами Instagram и Meta.",
          "Не используйте Сервис для рекламных кампаний, массовых рассылок, сообщений людям, которые не писали вам первыми, и для сбора предоплат с клиентов.",
          "Сообщите клиентам, что их сообщения и записи по вашему поручению обрабатывают сторонние сервисы, например разместите в профиле ссылку на уведомление о конфиденциальности.",
          "Следите, чтобы услуги, цены и правила в Сервисе были актуальными: ассистент отвечает только по ним.",
        ],
      },
      {
        heading: "5. Ответы ассистента",
        paragraphs: [
          "Ассистент может ошибаться. Он отвечает только по данным салона и передаёт диалог мастеру, когда не знает ответа, когда клиент просит человека, когда клиент жалуется или пишет о здоровье. Сам ассистент не говорит, что он ИИ, но на прямой вопрос отвечает честно; в настройках можно сделать так, чтобы он говорил об этом сразу. За общение с клиентами отвечает салон.",
        ],
      },
      {
        heading: "6. Данные",
        paragraphs: [
          "В отношении персональных данных ваших клиентов (сообщения, имена, телефоны, записи) салон является оператором, а мы обработчиком по договору об обработке данных. Как мы обращаемся с данными, описано в Политике конфиденциальности.",
        ],
      },
      {
        heading: "7. Доступность и изменения",
        paragraphs: [
          "Мы стараемся, чтобы Сервис был доступен, но не гарантируем бесперебойную работу. Работа с сообщениями Instagram зависит от Meta, которая может менять или ограничивать свою платформу. Мы можем менять Сервис и эти условия; о существенных изменениях сообщаем по почте не позднее чем за 14 дней.",
        ],
      },
      {
        heading: "8. Ответственность",
        paragraphs: [
          "В пределах, допускаемых законом, наша общая ответственность по любому требованию ограничена суммой, которую вы заплатили за Сервис в последние 3 месяца до предъявления требования. Мы не отвечаем за упущенную выгоду, сорвавшиеся записи и косвенные убытки.",
        ],
      },
      {
        heading: "9. Прекращение и ваши данные",
        paragraphs: [
          "Вы можете перестать пользоваться Сервисом в любой момент. По запросу мы выгрузим базу клиентов и записи в CSV и удалим данные. После запроса на удаление данные хранятся 30 дней на случай, если вы передумаете, а затем удаляются окончательно. Запрос на удаление сразу отменяет подписку; если вы отзовёте его в течение 30 дней, данные вернутся, но Сервис останется неактивным, пока вы не оформите новую подписку (без пробного периода).",
        ],
      },
      { heading: "10. Контакты", paragraphs: [`Поддержка: ${S.email}.`] },
    ],
  },
  refund: {
    title: "Политика возврата",
    intro: `Платежи за ${S.productName} обрабатывает Paddle.com, наш официальный продавец (Merchant of Record). Деньги возвращает Paddle тем же способом, которым вы платили.`,
    note: RU_NOTE,
    sections: [
      { heading: "Пробный период", paragraphs: ["Если отменить подписку во время бесплатного пробного периода, платить ничего не придётся."] },
      {
        heading: "Подписка",
        list: [
          "Отменить можно в любой момент; подписка действует до конца оплаченного месяца и не продлевается.",
          "Если сервис вам не подошёл, в течение 14 дней после первого платежа можно вернуть этот платёж полностью.",
          "Последующие ежемесячные платежи за частично использованный месяц не возвращаются, если этого не требует закон.",
        ],
      },
      {
        heading: "Как запросить возврат",
        paragraphs: [
          `Напишите на ${S.email} или ответьте на письмо Paddle с чеком, указав номер заказа. Paddle может рассмотреть запрос и по своим правилам для покупателей.`,
        ],
      },
    ],
  },
  privacy: {
    title: "Политика конфиденциальности",
    intro: `Эта политика объясняет, как мы, поставщик сервиса ${S.productName} («Сервис»), обрабатываем персональные данные в Сервисе, включая данные, полученные через платформу Instagram.`,
    note: RU_NOTE,
    sections: [
      {
        heading: "1. Роли",
        paragraphs: [
          "Данные аккаунтов владельцев и мастеров салона обрабатываем мы как оператор. В отношении данных клиентов салона (сообщения, имена, телефоны, записи) оператором является салон, а мы обрабатываем данные от его имени по договору об обработке данных.",
        ],
      },
      {
        heading: "2. Какие данные мы обрабатываем",
        list: [
          "Данные аккаунта: адрес электронной почты, имя, язык, сессии входа.",
          "Данные салона: услуги, цены, мастера, графики, правила и ответы на частые вопросы.",
          "Данные Instagram (через Instagram API и вход Unified Login, он же Facebook Login for Business, после того как салон подключит профессиональный аккаунт): ID и имя пользователя аккаунта, токен доступа (хранится в зашифрованном виде), сообщения клиентов салону и ответы, отправленные из Сервиса, Instagram-идентификаторы и имена пользователей клиентов.",
          "Записи и карточки клиентов: имя, телефон (если указан), история визитов, заметки, неявки.",
          "Голосовые сообщения расшифровываются в текст, аудио не хранится. Фотографии и другие файлы не хранятся.",
          "Платёжные данные собирает Paddle; мы получаем только статус подписки, без данных карты.",
          "Технические логи и учёт использования (например, число токенов ИИ по каждому салону).",
        ],
      },
      {
        heading: "3. Как мы используем данные",
        list: [
          "Чтобы отвечать клиентам салона и записывать их по правилам салона.",
          "Чтобы показывать диалоги, записи и уведомления мастерам салона.",
          "Для оплаты подписки, безопасности Сервиса, отслеживания ошибок и поддержки салона.",
          "Сообщения о здоровье (например, об аллергии) передаются мастеру и не отправляются ИИ-модели.",
          "Мы не продаём данные и не используем их для рекламы. Данные Instagram используем только для работы Сервиса у салона, который подключил аккаунт.",
        ],
      },
      {
        heading: "4. Поставщики услуг",
        paragraphs: [
          "Мы работаем с поставщиками: Anthropic (ИИ-модель, США), OpenAI (распознавание речи, США), Railway (серверы в регионе EU West, часть обработки в США по условиям Railway), Resend (отправка писем), Paddle (платежи, Merchant of Record), Meta (сообщения Instagram). Некоторые из них обрабатывают данные за пределами Сербии и ЕС, в том числе в США.",
        ],
      },
      {
        heading: "5. Сроки хранения",
        list: [
          "Данные салона и клиентов хранятся, пока салон пользуется Сервисом.",
          "После запроса на удаление данные салона окончательно стираются через 30 дней.",
          "Технические логи вебхуков хранятся 90 дней, данные об использовании ИИ: 180 дней.",
        ],
      },
      {
        id: "data-deletion",
        heading: "6. Как удалить свои данные",
        list: [
          `Салоны: напишите на ${S.email} с адреса электронной почты владельца и попросите удалить аккаунт. Мы подтвердим запрос в течение 7 дней; данные окончательно удаляются через 30 дней после запроса. Доступ можно отозвать и самостоятельно в любой момент: в Instagram в разделе «Настройки и действия» → «Разрешения для сайтов» → «Приложения и сайты» или в настройках своего бизнес-портфолио Meta (подключённые приложения).`,
          `Клиенты салона: обратитесь в салон или напишите на ${S.email}, указав своё имя пользователя в Instagram и название салона; мы передадим запрос салону и удалим данные по его указанию.`,
        ],
      },
      {
        heading: "7. Ваши права",
        paragraphs: [
          "Вы можете запросить доступ к своим данным, их исправление или удаление, ограничение обработки, перенос данных, а также возразить против обработки. Жалобу можно подать Уполномоченному по информации общественного значения и защите персональных данных Сербии (poverenik.rs).",
        ],
      },
      { heading: "8. Контакты", paragraphs: [`Поддержка: ${S.email}.`] },
    ],
  },
};

export const legalDocs: Record<SiteLang, Record<LegalKind, LegalDoc>> = { sr, en, ru };
